import React, { useState, useEffect, useContext } from 'react';
import Turnstile from 'react-turnstile';
import { isRutValid, sanitizeRutInput, RUT_MAX_LENGTH } from '../../utils';
import useLegalUrl from '../hooks/useLegalUrl';
import useStationsData from '../hooks/useStationsData';
import useCarsData from '../hooks/useCarsData';
import useTimeData from '../hooks/useTimeData';
import useDistanceData from '../hooks/useDistanceData';
import { CotizadorContext } from '../context';
import axios from 'axios';
import CurrencyFormat from './utils/CurrencyFormat';
import { getPdf } from './utils/getPdf';
import ModalContactMe from './modal/ModalContactMe';

const fleetSizeOptions = ['1-5', '6-10', '11-20', '21-50', '51+'];

const PURCHASE_TIMEFRAME_OPTIONS = [
    'En 1 semana',
    'Entre 1 y 3 meses',
    'Solo estoy cotizando',
];

const TwoStepForm = ({ uf_value }) => {
    const [state, setState] = useContext(CotizadorContext);
    const [currentStep, setCurrentStep] = useState(1);
    const [values, setValues] = useState({
        id: 0,
        company: "",
        email: "",
        rut: "",
        contact_name: "",
        contact: "-",
        phone_number: "",
        station: 0,
        region_id: 0,
        region: "",
        message: "",
        fleet_size: "",
        purchase_timeframe: "",
        model: "",
        period: "",
        kms: "",
    });

    const { stations } = useStationsData();
    const { cars, getSelectedCar, setSelectedCar } = useCarsData();
    const { time_options, getSelectedTime, setSelectedTime } = useTimeData();
    const { distance_options, getSelectedDistance, setSelectedDistance } = useDistanceData();
    const links = useLegalUrl();

    const [checkboxValue, setCheckboxValue] = useState(false);
    const [filteredStations, setFilteredStations] = useState(stations);
    const [btn, setBtn] = useState(false);
    const [error, setError] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState(false);
    const [sendContactForm, setSendContactForm] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [contactData, setContactData] = useState(null);
    const [showContactModal, setShowContactModal] = useState(false);

    const sitekey = document.getElementById("recaptcha_sitekey")?.getAttribute('value');

    const uniqueRegions = [];
    stations?.forEach(station => {
        const region = station.city.region;
        if (region) {
            const existingRegionIndex = uniqueRegions.findIndex(r => r.id === region.id);
            if (existingRegionIndex === -1) {
                uniqueRegions.push(region);
            }
        }
    });
    uniqueRegions.sort((a, b) => a.name.localeCompare(b.name));

    const handleOnFormChange = (e) => {
        if (e.target.value) {
            e.target.classList.add('-interacted');
        } else {
            e.target.classList.remove('-interacted');
        }
    }

    const handleInput = (e) => {
        let inputName = e.target.name;
        let value = e.target.value;

        if (inputName === "region") {
            uniqueRegions?.forEach(region => {
                if (region.id == value) {
                    setValues({ ...values, region: region.name, region_id: region.id, station: 0 });
                }
            });
        } else if (inputName === "station") {
            setValues({ ...values, station: Number(value) });
        } else if (inputName === "rut") {
            setValues({ ...values, rut: sanitizeRutInput(value) });
        } else {
            setValues({ ...values, [inputName]: value });
        }
    }

    const handleCheckboxChange = (e) => {
        setCheckboxValue(e.target.checked);
    }

    const handlePurchaseTimeframe = (option) => {
        setValues(prevState => ({ ...prevState, purchase_timeframe: option }));
    }

    const handleCarSelect = async (e) => {
        const nextCarId = Number(e.target.value);

        setValues(prevState => ({
            ...prevState,
            model: nextCarId || "",
        }));

        if (!nextCarId) {
            return;
        }

        const selectedCar = cars.find(car => car.id === nextCarId);
        await setSelectedCar(nextCarId);

        if (selectedCar) {
            dataLayer.push({
                'event': 'trackEvent',
                'eventCategory': 'cotizar_one_business',
                'eventAction': 'elegir_modelo',
                'eventLabel': selectedCar.name
            });
        }
    };

    const handleTimeChange = async (e) => {
        const selectedOption = time_options.find(option => option.value === Number(e.target.value));

        if (!selectedOption) {
            return;
        }

        setValues(prevState => ({
            ...prevState,
            period: selectedOption.value,
        }));

        await setSelectedTime(selectedOption);

        dataLayer.push({
            'event': 'trackEvent',
            'eventCategory': 'cotizar_one_business',
            'eventAction': 'tiempo_uso',
            'eventLabel': `${selectedOption.value}m`
        });
    };

    const handleDistanceChange = async (e) => {
        const selectedOption = distance_options.find(option => option.value === Number(e.target.value));

        if (!selectedOption) {
            return;
        }

        setValues(prevState => ({
            ...prevState,
            kms: selectedOption.value,
        }));

        await setSelectedDistance(selectedOption);

        dataLayer.push({
            'event': 'trackEvent',
            'eventCategory': 'cotizar_one_business',
            'eventAction': 'kilometros',
            'eventLabel': `${selectedOption.value}`
        });
    };

    const handleTokenSubmit = (token) => {
        fetch("/recaptcha/", {
            method: "POST",
            body: JSON.stringify({ token: token }),
            headers: {
                "Content-Type": "application/json",
                'X-CSRFToken': document.querySelector('meta[name="csrf-token"]').content
            },
        })
            .then(response => response.json())
            .then((data) => {
                if (data.success) {
                    setTurnstileToken(true);
                }
            })
            .catch((error) => {
                console.log("ERROR", error);
            });
    }

    useEffect(() => {
        // El RUT se valida siempre con isRutValid, pero el mensaje de error solo se
        // muestra cuando ya se escribió un RUT completo (el más corto válido tiene 8 caracteres)
        const rutValid = isRutValid(values.rut);
        const rutError = !rutValid && values.rut.length >= 8;

        if (stations && values.region_id) {
            setFilteredStations(stations.filter(station => {
                return station.city.region.id == values.region_id;
            }));
        } else {
            setFilteredStations(stations);
        }

        setError(rutError);
        // Si Turnstile está habilitado, requerirlo. Si está comentado, no lo requerimos
        const turnstileRequired = sitekey ? turnstileToken : true;
        setBtn(
            values.company &&
            values.email &&
            rutValid &&
            values.contact_name &&
            values.phone_number &&
            values.region &&
            values.station &&
            values.purchase_timeframe &&
            checkboxValue &&
            turnstileRequired
        );
    }, [values, checkboxValue, stations, turnstileToken]);

    const handleContinueToStep2 = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isRutValid(values.rut)) {
            setError(true);
            return;
        }

        try {
            const selectedCar = getSelectedCar();
            const selectedTime = getSelectedTime();
            const selectedDistance = getSelectedDistance();
            const dataToSend = {
                ...values,
                station: values.station || null,
                fleet_size: values.fleet_size || "-",
                model: selectedCar ? selectedCar.id : null,
                period: selectedTime ? selectedTime.value : "-",
                kms: selectedDistance ? selectedDistance.value : "-",
                journey: window.journey?.getData ? window.journey.getData() : null,
            };

            const response = await axios.post('/api/kinto/forms/one_business/', dataToSend, {
                headers: {
                    'X-CSRFToken': document.querySelector('meta[name="csrf-token"]').content
                }
            });

            const updatedValues = { ...dataToSend, id: response.data.id };
            setValues(updatedValues);
            setState(prevState => ({
                ...prevState,
                personal_data: updatedValues,
            }));

            dataLayer.push({
                'event': 'trackEvent',
                'eventCategory': 'cotizar_one_business',
                'eventAction': 'cta',
                'eventLabel': 'continuar_cotizador'
            });

            setCurrentStep(2);
        } catch (error) {
            console.error('Error al crear el lead:', error);
        }
    };

    const handleBackToStep1 = () => {
        setCurrentStep(1);
    };

    const handleCancel = () => {
        // Reset form or redirect
        setValues({
            id: 0,
            company: "",
            email: "",
            rut: "",
            contact_name: "",
            contact: "-",
            phone_number: "",
            station: 0,
            region_id: 0,
            region: "",
            message: "",
            fleet_size: "",
            model: "",
            period: "",
            kms: "",
        });
        setCheckboxValue(false);
        setTurnstileToken(false);
        setSendContactForm(false);
        setDownloadingPdf(false);
        setContactData(null);
        setCurrentStep(1);
    };

    const selectedCar = getSelectedCar();
    const selectedTime = getSelectedTime();
    const selectedDistance = getSelectedDistance();
    // Total estimado: cuota de la simulación (valores del vehículo por duración/kilometraje),
    // igual que en One Personal. Los months_XX_rate son solo los precios "desde".
    const estimatedTotal = selectedCar && state.simulation_total > 0 ? state.simulation_total : null;

    // Cuota en UF: usa el valor exacto importado (amount_uf); si no existe, lo deriva del total
    const amountUf = state.simulation_results?.amount_uf ?? (estimatedTotal && uf_value ? estimatedTotal / uf_value : null);
    const amountUfLabel = amountUf
        ? Number(amountUf).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '-';

    const downloadPdf = async () => {
        if (!selectedCar || !selectedTime || !selectedDistance || !estimatedTotal || downloadingPdf) {
            return;
        }

        setDownloadingPdf(true);

        try {
            const carImg = selectedCar.photo_1?.url
                ? selectedCar.photo_1.url
                : `${window._env_.STATIC_HOST}/static/kinto/img/cotizador/default_selected_car.png`;

            const { data } = await getPdf(
                selectedCar.id,
                selectedTime,
                selectedDistance,
                0,
                estimatedTotal,
                carImg,
            );

            const file = new Blob([data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');

            link.href = fileURL;
            link.download = `cotizacion-kinto-${selectedCar.name.replace(/\s+/g, '-')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(fileURL);

            dataLayer.push({
                event: 'trackEvent',
                eventCategory: 'cotizar_one_business',
                eventAction: 'cta',
                eventLabel: 'descargar_detalle',
            });
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handleContactSelect = async (contactMethod) => {
        await sendContact(contactMethod);
        setShowContactModal(false);
    };

    const sendContact = async (contactMethod = 'Solicita ser contactado') => {
        if (!contactData || !selectedCar || !selectedTime || !selectedDistance || !values.fleet_size || sendContactForm) {
            return;
        }

        const dataToSend = {
            ...contactData,
            contact: contactMethod,
            journey: window.journey?.getData ? window.journey.getData() : null,
        };

        try {
            const headers = {
                'X-CSRFToken': document.querySelector('meta[name="csrf-token"]').content
            };
            const response = values.id
                ? await axios.put(`/api/kinto/forms/one_business_update/${values.id}/`, dataToSend, { headers })
                : await axios.post('/api/kinto/forms/one_business/', dataToSend, { headers });

            const updatedValues = {
                ...values,
                ...dataToSend,
                id: response.data.id || values.id,
            };

            setValues(updatedValues);
            setState(prevState => ({
                ...prevState,
                personal_data: updatedValues,
            }));

            dataLayer.push({
                'event': 'trackEvent',
                'eventCategory': 'cotizar_one_business',
                'eventAction': 'cta',
                'eventLabel': 'contactar_asesor'
            });

            setSendContactForm(true);
        } catch (error) {
            console.error('Error al enviar el formulario:', error);
        }
    };

    useEffect(() => {
        setValues(prevState => ({
            ...prevState,
            model: selectedCar ? selectedCar.id : prevState.model,
            period: selectedTime ? selectedTime.value : prevState.period,
            kms: selectedDistance ? selectedDistance.value : prevState.kms,
        }));
    }, [selectedCar, selectedTime, selectedDistance]);

    useEffect(() => {
        setContactData({
            company: values.company,
            email: values.email,
            rut: values.rut,
            contact_name: values.contact_name,
            contact: values.contact,
            phone_number: values.phone_number,
            station: values.station || null,
            message: values.message,
            fleet_size: values.fleet_size,
            purchase_timeframe: values.purchase_timeframe,
            model: selectedCar ? selectedCar.id : null,
            period: selectedTime ? selectedTime.value : null,
            kms: selectedDistance ? selectedDistance.value : null,
            journey: window.journey?.getData ? window.journey.getData() : null,
        });
    }, [values, selectedCar, selectedTime, selectedDistance]);

    useEffect(() => {
        if (!values.id) {
            return;
        }

        const updateLead = async () => {
            const dataToSend = {
                ...values,
                contact: values.contact || "-",
                station: values.station || null,
                fleet_size: values.fleet_size || "-",
                model: selectedCar ? selectedCar.id : null,
                period: selectedTime ? selectedTime.value : "-",
                kms: selectedDistance ? selectedDistance.value : "-",
                journey: window.journey?.getData ? window.journey.getData() : null,
            };

            try {
                await axios.put(`/api/kinto/forms/one_business_update/${values.id}/`, dataToSend, {
                    headers: {
                        'X-CSRFToken': document.querySelector('meta[name="csrf-token"]').content
                    }
                });

                setValues(prevState => ({
                    ...prevState,
                    ...dataToSend,
                }));

                setState(prevState => ({
                    ...prevState,
                    personal_data: {
                        ...prevState.personal_data,
                        ...dataToSend,
                    },
                }));
            } catch (error) {
                console.error('Error al actualizar el lead:', error);
            }
        };

        updateLead();
    }, [selectedCar?.id, selectedTime?.value, selectedDistance?.value, values.fleet_size, values.id]);

    useEffect(() => {
        setSendContactForm(false);
    }, [selectedCar, selectedTime, selectedDistance]);

    return (
        <>
            <div className="step-indicator">
                <button 
                    className="back-button" 
                    onClick={currentStep === 2 ? handleBackToStep1 : null}
                    style={{ display: currentStep === 1 ? 'none' : 'flex' }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>Volver atrás</p>
                </button>
                <span className="step-text">Paso {currentStep} de 2</span>
            </div>
            <div className="two-step-form">
                <div className="two-step-form-content">

                    <div className='quoter-header'>
                        <h2>Cotiza tu plan KINTO ONE BUSINESS</h2>
                        <p>
                            {currentStep === 1 
                                ? "Dejanos tus datos y sigue con la simulación en el cotizador."
                                : "Completa los datos de tu flota para continuar con la cotización."
                            }
                        </p>
                    </div>


                    {currentStep === 1 ? (
                        <form onSubmit={handleContinueToStep2} onChange={handleOnFormChange} className="quoter-form step-one-form">
                            <div className="form-row-group">
                                <h3 className="section-title --text-header">Datos de contacto</h3>
                                
                                <div className="form-row">
                                    <label htmlFor="company">Empresa*</label>
                                    <input
                                        type="text"
                                        id="company"
                                        className="form-control"
                                        name="company"
                                        placeholder="Ej.: Empresa S.A."
                                        value={values.company}
                                        onChange={handleInput}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <label htmlFor="rut">RUT*</label>
                                    <input
                                        type="text"
                                        id="rut"
                                        name="rut"
                                        placeholder="Ej.: 14737553-0"
                                        value={values.rut}
                                        onChange={handleInput}
                                        className={`form-control ${error ? 'error' : ''}`}
                                        maxLength={RUT_MAX_LENGTH}
                                        required
                                    />
                                    {error && <span className="error-message">RUT Inválido</span>}
                                </div>
                            </div>

                            <div className="form-row-group">
                                <div className="form-row">
                                    <label htmlFor="contact_name">Persona de contacto*</label>
                                    <input
                                        type="text"
                                        id="contact_name"
                                        className="form-control"
                                        name="contact_name"
                                        placeholder="Ej.: Luis Salas"
                                        value={values.contact_name}
                                        onChange={handleInput}
                                        required
                                    />
                                </div>

                                <div className="form-row">
                                    <label htmlFor="email">Email*</label>
                                    <input
                                        type="email"
                                        id="email"
                                        className="form-control"
                                        name="email"
                                        placeholder="Ej.: nombre@correo.com"
                                        value={values.email}
                                        onChange={handleInput}
                                        required
                                    />
                                </div>
                            </div>

                            <div className='form-row-group'>
                                <div className="form-row">
                                    <label htmlFor="phone_number">Telefono*</label>
                                    <input
                                        type="text"
                                        id="phone_number"
                                        className="form-control"
                                        name="phone_number"
                                        placeholder="Ej.: +56 9 1234 5678"
                                        value={values.phone_number}
                                        onChange={handleInput}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-row-group">
                                <h3 className="section-title --text-header">Preferencias de atención</h3>
                                
                                <div className="form-row">
                                    <label htmlFor="region">Región*</label>
                                    <select
                                        id="region"
                                        className="form-control"
                                        name="region"
                                        value={values.region_id}
                                        onChange={handleInput}
                                        required
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {uniqueRegions.map((reg) => (
                                            <option key={reg.id} value={reg.id}>{reg.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="station">Sucursal*</label>
                                    <select
                                        id="station"
                                        className="form-control"
                                        name="station"
                                        value={values.station}
                                        onChange={handleInput}
                                        disabled={!values.region_id}
                                        required
                                    >
                                        <option value="">Selecciona una sucursal</option>
                                        {filteredStations.map((station) => (
                                            <option key={station.id} value={station.id}>{station.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row-group">
                                <h3 className="section-title --text-header">¿Cuándo quieres iniciar el proceso de compra?*</h3>

                                <div className="purchase-timeframe-options">
                                    {PURCHASE_TIMEFRAME_OPTIONS.map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            className={`purchase-timeframe-option${values.purchase_timeframe === option ? ' -selected' : ''}`}
                                            onClick={() => handlePurchaseTimeframe(option)}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="form-checkbox">
                                <label className="checkbox-container">
                                    <input
                                        type="checkbox"
                                        checked={checkboxValue}
                                        onChange={handleCheckboxChange}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="checkbox-label">
                                        Acepto las <a href={links.legal_url} target="_blank" rel="noopener noreferrer">políticas de privacidad</a> y el tratamiento de mis datos.
                                    </span>
                                </label>
                            </div>

                            {sitekey && (
                                <div className="form-turnstile">
                                    <Turnstile
                                        theme="light"
                                        sitekey={sitekey}
                                        onVerify={(token) => handleTokenSubmit(token)}
                                    />
                                </div>
                            )}

                            <div className="quoter-actions">
                                <button
                                    type="button"
                                    className="button --new --light"
                                    onClick={handleCancel}
                                >
                                    CANCELAR
                                </button>
                                <button
                                    type="submit"
                                    className="button --new --dark"
                                    disabled={!btn}
                                >
                                    CONTINUAR AL COTIZADOR
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form className="step-two-form quoter-form">
                            <div className="form-row-group">
                                <div className="form-row">
                                    <label htmlFor="model">Modelo*</label>
                                    <select
                                        id="model"
                                        className="form-control"
                                        name="model"
                                        value={selectedCar ? selectedCar.id : values.model}
                                        onChange={handleCarSelect}
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {cars.map((car) => (
                                            <option key={car.id} value={car.id}>{car.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <div>
                                        <img
                                            src={selectedCar ? selectedCar.photo_1.url : `${window._env_.STATIC_HOST}/static/kinto/img/cotizador/default_selected_car2.png`}
                                            alt={selectedCar ? selectedCar.name : 'Modelo seleccionado'}
                                            style={{ width: '100%', objectFit: 'contain' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-row-group">
                                <div className="form-row">
                                    <label htmlFor="period">Duración*</label>
                                    <select
                                        id="period"
                                        className="form-control"
                                        name="period"
                                        value={selectedTime ? selectedTime.value : values.period}
                                        onChange={handleTimeChange}
                                    >
                                        {time_options.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="kms">Kilometraje*</label>
                                    <select
                                        id="kms"
                                        className="form-control"
                                        name="kms"
                                        value={selectedDistance ? selectedDistance.value : values.kms}
                                        onChange={handleDistanceChange}
                                    >
                                        {distance_options
                                            .filter((option) => option.enabled)
                                            .map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row-group">
                                <div className="form-row --full">
                                    <label htmlFor="fleet_size">Tamaño de flota*</label>
                                    <select
                                        id="fleet_size"
                                        className="form-control"
                                        name="fleet_size"
                                        value={values.fleet_size}
                                        onChange={handleInput}
                                    >
                                        <option value="">Selecciona una opción</option>
                                        {fleetSizeOptions.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row-group">
                                <div className="form-row --full">
                                    <label htmlFor="message">Comentarios</label>
                                    <textarea
                                        id="message"
                                        className="form-control"
                                        name="message"
                                        rows="4"
                                        placeholder="Comentarios adicionales"
                                        value={values.message}
                                        onChange={handleInput}
                                    />
                                </div>
                            </div>
                        </form>
                    )}
                    {currentStep === 2 && estimatedTotal ? (
                        <>
                            <div className="quoter-result">
                                <div className="result-label">Total estimado:</div>
                                <div className="result-price">
                                    <CurrencyFormat value={estimatedTotal} /> <span>/mes</span> <span style={{ color: '#738184', fontWeight: 300 }}>*</span>
                                </div>
                                <div className="result-note">
                                    Incluye seguro y asistencia KINTO.
                                </div>
                                <div className="result-disclaimer">*Calculado con un valor de {amountUfLabel} UF a un precio de UF=${uf_value.toLocaleString('es-CL')}</div>
                            </div>
                            <div className="quoter-actions">
                                <button
                                    type="button"
                                    onClick={downloadPdf}
                                    className={downloadingPdf ? 'button --new --light button--disabled' : 'button --new --light'}
                                    disabled={downloadingPdf || !selectedCar || !estimatedTotal}
                                >
                                    {downloadingPdf ? 'Generando PDF...' : 'Descargar cotización'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowContactModal(true)}
                                    className={sendContactForm ? "button --new --dark button--disabled" : "button --new --dark"}
                                    disabled={sendContactForm || !selectedCar || !selectedTime || !selectedDistance || !values.fleet_size}
                                >
                                    {sendContactForm ? 'Formulario enviado ✓' : 'Quiero ser contactado'}
                                </button>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
            {showContactModal && (
                <ModalContactMe
                    onClose={() => setShowContactModal(false)}
                    onSelect={handleContactSelect}
                />
            )}
        </>
    );
};

export default TwoStepForm;
